import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import LocContext from "../context/locContext";

const IMPERSONATION_ADMIN_TOKEN = "impersonation_admin_token";
const IMPERSONATION_ADMIN_USER = "impersonation_admin_user";

const Loader = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255,255,255,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
    }}
  >
    <div
      className="spinner-border text-primary"
      style={{
        width: "4rem",
        height: "4rem",
        color: "#06d1c6",
        borderWidth: "6px",
      }}
      role="status"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

export default function ClientNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useContext(LocContext) || {};
  const isImpersonation = !!userData?.isImpersonation;
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("client_sidebar_collapsed") === "true",
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 991 : false,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inlineOpen, setInlineOpen] = useState(null); // "account" | "transact" | null
  const [floatingOpen, setFloatingOpen] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const [floatingTop, setFloatingTop] = useState(120);
  const accountRef = useRef(null);
  const transactRef = useRef(null);
  const floatingRef = useRef(null);
  const profileMenuRef = useRef(null);

  const displayName = [userData?.first_name, userData?.last_name].filter(Boolean).join(" ") || "Client";
  const userInitial = (userData?.first_name || "C").charAt(0).toUpperCase();

  const exitImpersonation = () => {
    const adminToken = sessionStorage.getItem(IMPERSONATION_ADMIN_TOKEN);
    const adminUserEncrypted = sessionStorage.getItem(IMPERSONATION_ADMIN_USER);
    const impersonationToken = localStorage.getItem("authToken");
    if (impersonationToken) {
      axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/stop-impersonation`, {}, {
        headers: { Authorization: `Bearer ${impersonationToken}` },
      }).catch(() => {});
    }
    sessionStorage.removeItem(IMPERSONATION_ADMIN_TOKEN);
    sessionStorage.removeItem(IMPERSONATION_ADMIN_USER);
    if (adminToken != null) {
      localStorage.setItem("authToken", adminToken);
    }
    if (adminUserEncrypted != null && adminUserEncrypted !== "") {
      localStorage.setItem(process.env.REACT_APP_LOGIN_DATA_STORE, adminUserEncrypted);
    } else {
      localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
    }
    window.location.href = "/client-list";
  };

  // ==============================
  // ✅ 3. LOGOUT FUNCTION START
  // ==============================
  const handleLogout = async () => {
    if (isImpersonation) {
      Swal.fire({
        title: "Exit client view?",
        html: `<p style="color:#555;">This will return you to the Admin Client List.</p>`,
        showCancelButton: true,
        confirmButtonText: "Exit & Return to Admin",
        cancelButtonText: "Cancel",
        buttonsStyling: false,
        customClass: {
          popup: "custom-swal-popup",
          confirmButton: "custom-confirm-btn",
          cancelButton: "custom-cancel-btn",
        },
      }).then((result) => {
        if (result.isConfirmed) exitImpersonation();
      });
      return;
    }
    Swal.fire({
      title: "Logout Confirmation",
      html: `
      <div style="text-align:center;">
        <div style="width:70px;height:70px;margin:0 auto 15px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-sign-out-alt" style="font-size:32px;color:#06d1c6;"></i>
        </div>
        <p style="font-size:16px;color:#555;margin:0;">
          Are you sure you want to logout from your account?
        </p>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "custom-confirm-btn",
        cancelButton: "custom-cancel-btn",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);

        axios
          .post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/logout`)
          .then((res) => {
            if (res.status === 200) {
              localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
              localStorage.removeItem("authToken");

              showLogoutSuccess(); // call success popup
              navigate("/");
            }
          })
          .catch(() => {
            Swal.fire("Error", "Something went wrong", "error");
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const showLogoutSuccess = () => {
    Swal.fire({
      html: `
      <div style="text-align:center;padding:20px 10px;">
        <div style="width:80px;height:80px;margin:0 auto 20px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-check" style="font-size:36px;color:#06d1c6;"></i>
        </div>
        <h2 style="margin:0;font-size:22px;color:#333;">Logged out successfully</h2>
        <p style="margin-top:8px;color:#777;font-size:14px;">
          You have been safely logged out.
        </p>
      </div>
    `,
      showConfirmButton: false,
      timer: 1500,
      backdrop: `rgba(0,0,0,0.4)`,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };
  // ==============================
  // ✅ LOGOUT FUNCTION END
  // ==============================

  // Update CSS variable and body class for sidebar width
  useEffect(() => {
    const width = collapsed ? "72px" : "240px";
    document.documentElement.style.setProperty("--client-sidebar-width", width);
    if (collapsed) document.body.classList.add("client-sidebar-collapsed");
    else document.body.classList.remove("client-sidebar-collapsed");
  }, [collapsed]);

  // Resize: detect mobile
  useEffect(() => {
    function onResize() {
      const small = window.innerWidth <= 991;
      setIsMobile(small);
      if (small) setMobileOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // When collapsed close inline; when expanded close floating
  useEffect(() => {
    if (collapsed) setInlineOpen(null);
    else setFloatingOpen(null);
  }, [collapsed]);

  // Compute floating submenu position
  useEffect(() => {
    const ref =
      floatingOpen === "account"
        ? accountRef
        : floatingOpen === "transact"
          ? transactRef
          : null;
    if (ref?.current) {
      const rect = ref.current.getBoundingClientRect();
      const top = Math.max(
        64,
        Math.min(window.innerHeight - 200, rect.top + rect.height / 2),
      );
      setFloatingTop(top);
    }
  }, [floatingOpen, collapsed]);

  // Click outside or Escape closes floating menu and mobile overlay
  useEffect(() => {
    function onDocClick(e) {
      if (
        floatingOpen &&
        floatingRef.current &&
        !floatingRef.current.contains(e.target)
      ) {
        const trigger =
          floatingOpen === "account"
            ? accountRef.current
            : transactRef.current;
        if (trigger && !trigger.contains(e.target)) setFloatingOpen(null);
      }
      if (mobileOpen) {
        const sidebarEl = document.querySelector(".client-sidebar");
        if (sidebarEl && !sidebarEl.contains(e.target)) setMobileOpen(false);
      }
      if (
        profileOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setProfileOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setFloatingOpen(null);
        setInlineOpen(null);
        setMobileOpen(false);
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [floatingOpen, mobileOpen, profileOpen]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("client_sidebar_collapsed", next ? "true" : "false");
  };

  const handleMobileToggle = () => setMobileOpen((v) => !v);

  const toggleDropdown = (menuName) => {
    if (isMobile) {
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
      return;
    }
    if (collapsed) {
      setFloatingOpen((v) => (v === menuName ? null : menuName));
      setInlineOpen(null);
    } else {
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
    }
  };

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/css/style.css";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    setTimeout(() => setLoading(false), 600);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) return <Loader />;

  return (
    <>
      <style>{`
      .custom-swal-popup {
  border-radius: 16px !important;
  padding: 25px !important;

}

.custom-confirm-btn {
  background: linear-gradient(90deg, #06b6a4, #06d1c6) !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  margin-right: 10px;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(221, 36, 118, 0.3);
}

.custom-cancel-btn {
  background: #ef4444 !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-cancel-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(221, 36, 118, 0.3);
}

.brand-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.brand-logo img {
  max-width: 140px;
  height: auto;
}

:root {
  --client-sidebar-width: 240px;
  --client-sidebar-collapsed-width: 72px;
  --client-teal: #139bb2;
  --client-teal-dark: #0e8499;
}

.page-content {
  margin-left: var(--client-sidebar-width, 240px);
  padding: 88px 28px 32px;
  min-height: 100vh;
  background: #f3f6f9;
  transition: margin-left 220ms ease;
}
body.client-sidebar-collapsed .page-content {
  margin-left: var(--client-sidebar-collapsed-width, 72px);
}

.client-topbar {
  position: fixed;
  top: 0;
  left: var(--client-sidebar-width, 240px);
  right: 0;
  height: 64px;
  background: #fff;
  border-bottom: 1px solid #e8eef3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 1400;
  transition: left 220ms ease;
}
body.client-sidebar-collapsed .client-topbar {
  left: var(--client-sidebar-collapsed-width, 72px);
}

.client-topbar-left,
.client-topbar-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.client-topbar-menu {
  border: none;
  background: transparent;
  color: #334155;
  font-size: 20px;
  cursor: pointer;
  padding: 6px;
  line-height: 1;
}

.client-topbar-icon {
  position: relative;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.client-topbar-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: var(--client-teal);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.client-topbar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 999px;
  position: relative;
}

.client-topbar-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--client-teal);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.client-topbar-username {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.client-topbar-user i {
  font-size: 12px;
  color: #64748b;
}

.client-profile-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e8eef3;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  padding: 8px;
  z-index: 1500;
}

.client-profile-menu a,
.client-profile-menu button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  text-align: left;
}

.client-profile-menu a:hover,
.client-profile-menu button:hover {
  background: #f1f5f9;
  color: var(--client-teal);
}

.client-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: var(--client-sidebar-width);
  min-width: var(--client-sidebar-width);
  height: 100vh;
  background: var(--client-teal);
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 18px 14px;
  box-sizing: border-box;
  z-index: 1500;
  transition: width 220ms ease, min-width 220ms ease, padding 220ms ease;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
}

.client-sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: none;
}
.client-sidebar-nav::-webkit-scrollbar {
  display: none;
}

.client-sidebar.collapsed {
  width: var(--client-sidebar-collapsed-width);
  min-width: var(--client-sidebar-collapsed-width);
  padding: 18px 10px;
}

.client-logo-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 4px 6px;
}
.client-logo-wrap img {
  width: 150px;
  max-width: 100%;
  border-radius: 8px;
  background: #fff;
  padding: 6px 8px;
  transition: width 220ms ease, height 220ms ease;
}
.client-sidebar.collapsed .client-logo-wrap img {
  width: 42px;
  height: 42px;
  object-fit: contain;
  padding: 4px;
}
.client-sidebar.collapsed .client-logo-wrap .client-collapse-btn {
  display: none;
}

.client-collapse-btn {
  margin-left: auto;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px;
  font-size: 16px;
}

.client-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border-radius: 12px;
  color: #ffffff;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: background 140ms ease, color 140ms ease;
  position: relative;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
}
.client-menu-item .icon {
  width: 22px;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: transparent;
  border-radius: 0;
  margin: 0;
  color: inherit;
}
.client-menu-item .label {
  line-height: 1.2;
  color: inherit;
  flex: 1;
}

.client-menu-item:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  text-decoration: none;
}
.client-menu-item.active {
  background: #ffffff;
  color: var(--client-teal);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  text-decoration: none;
}
.client-menu-item.active .label,
.client-menu-item.active .icon,
.client-menu-item.active i {
  color: var(--client-teal);
}

.client-sidebar.collapsed .client-menu-item {
  justify-content: center;
  padding: 12px 8px;
}
.client-sidebar.collapsed .client-menu-item .label,
.client-sidebar.collapsed .client-menu-item .fa-chevron-down,
.client-sidebar.collapsed .client-menu-item .fa-chevron-up {
  display: none;
}

.client-sidebar.collapsed .client-menu-item::after {
  content: attr(data-label);
  position: absolute;
  left: calc(var(--client-sidebar-collapsed-width) + 12px);
  top: 50%;
  transform: translateY(-50%) translateX(-6px);
  background: #ffffff;
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(7,12,34,0.12);
  white-space: nowrap;
  font-weight: 700;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease, transform 160ms ease;
  z-index: 9999;
}
.client-sidebar.collapsed .client-menu-item:hover::after {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.client-submenu {
  margin: 2px 0 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 8px;
  border-left: 2px solid rgba(255,255,255,0.2);
}
.client-submenu .client-menu-item {
  padding: 9px 12px;
  font-size: 13px;
  border-radius: 10px;
}

.client-floating-submenu {
  position: fixed;
  left: calc(var(--client-sidebar-collapsed-width) + 8px);
  transform: translateY(-50%);
  min-width: 210px;
  max-width: 280px;
  max-height: 80vh;
  overflow-y: auto;
  background: #ffffff;
  color: #0f172a;
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(7,12,34,0.16);
  padding: 10px;
  z-index: 2600;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.client-floating-submenu .client-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 10px;
  border-radius: 10px;
  color: #334155;
}
.client-floating-submenu .client-menu-item:hover,
.client-floating-submenu .client-menu-item.active {
  background: rgba(19, 155, 178, 0.1);
  color: var(--client-teal);
  box-shadow: none;
}
.client-floating-submenu .client-menu-item .icon {
  width: 22px;
  height: 22px;
  font-size: 15px;
  color: inherit;
}
.client-floating-submenu .client-menu-item .label {
  display: block;
  margin-top: 0;
  font-weight: 600;
  font-size: 13px;
  color: inherit;
}

.client-sidebar.overlay {
  transform: translateX(0);
  left: 0;
}

.client-mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 20, 0.45);
  z-index: 1400;
}

@media (max-width: 991px) {
  .client-topbar {
    display: none;
  }
  .client-sidebar {
    height: 100vh !important;
    max-height: 100vh !important;
    overflow-y: auto !important;
    padding-bottom: 50px !important;
    transform: translateX(-100%);
    transition: transform 220ms ease;
    width: 260px;
    min-width: 260px;
  }
  .client-sidebar.overlay {
    transform: translateX(0);
    left: 0;
  }
  .client-sidebar.collapsed {
    width: 260px;
    min-width: 260px;
    padding: 18px 14px;
  }
  .page-content {
    padding: 88px 16px 24px;
    margin-left: 0 !important;
  }
  .client-menu-item {
    padding: 11px 12px;
    font-size: 14px;
  }
}

@media (max-width: 575px) {
  .client-sidebar {
    width: 260px;
    min-width: 260px;
  }
  .client-logo-wrap img {
    width: 130px;
  }
}

.nav {
  display: block;
  flex-wrap: wrap;
  padding-left: 0;
  margin-bottom: 0;
  list-style: none;
}
.sidebar-logout {
  margin-top: 12px;
}

@media only screen and (max-width: 767px) {
  .brand-logo {
    display: block;
  }
}

`}</style>

      {/* Mobile top bar */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            height: 64,
            background: "white",
            zIndex: 1401,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "8px 14px",
            borderBottom: "1px solid #e8eef3",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={handleMobileToggle}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              style={{
                border: "none",
                background: "#139bb2",
                color: "#fff",
                borderRadius: 8,
                padding: "8px 10px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <i className={`fa ${mobileOpen ? "fa-times" : "fa-bars"}`} />
            </button>
            <NavLink to="/" style={{ textDecoration: "none", color: "#0d233e" }}>
              <img src="/images/gpslogo.png" alt="GPS logo" style={{ height: 36 }} />
            </NavLink>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "#139bb2",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {userInitial}
            </span>
          </div>
        </div>
      )}

      {/* Desktop top bar */}
      {!isMobile && (
        <header className="client-topbar">
          <div className="client-topbar-left">
            <button
              type="button"
              className="client-topbar-menu"
              onClick={toggleCollapse}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            >
              <i className="fa-solid fa-bars" />
            </button>
          </div>
          <div className="client-topbar-right">
            <button type="button" className="client-topbar-icon" aria-label="Notifications">
              <i className="fa-regular fa-bell" />
              <span className="client-topbar-badge">3</span>
            </button>
            <button type="button" className="client-topbar-icon" aria-label="Messages">
              <i className="fa-regular fa-comment-dots" />
            </button>
            <div ref={profileMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="client-topbar-user"
                onClick={() => setProfileOpen((v) => !v)}
                aria-expanded={profileOpen}
              >
                <span className="client-topbar-avatar">{userInitial}</span>
                <span className="client-topbar-username">{displayName}</span>
                <i className={`fa-solid fa-chevron-${profileOpen ? "up" : "down"}`} />
              </button>
              {profileOpen && (
                <div className="client-profile-menu">
                  <NavLink to="/account/profile-kyc" onClick={() => setProfileOpen(false)}>
                    <i className="fa-solid fa-user" /> Profile
                  </NavLink>
                  <NavLink to="/account/security" onClick={() => setProfileOpen(false)}>
                    <i className="fa-solid fa-shield-halved" /> Security
                  </NavLink>
                  <button type="button" onClick={() => { setProfileOpen(false); handleLogout(); }}>
                    <i className="fa-solid fa-arrow-right-from-bracket" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Sidebar (desktop: collapsible; mobile: overlay) */}
      <aside
        id="client-sidebar"
        className={`client-sidebar ${collapsed && !isMobile ? "collapsed" : ""} ${mobileOpen && isMobile ? "overlay" : ""}`}
        aria-label="Primary"
        style={{ top: isMobile ? 0 : undefined }}
      >
        {isImpersonation && (
          <div
            style={{
              padding: "10px 12px",
              marginBottom: 8,
              background: "rgba(0,0,0,0.2)",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <button
              type="button"
              onClick={exitImpersonation}
              className="client-menu-item"
              style={{
                width: "100%",
                justifyContent: "center",
                gap: 8,
                fontSize: 13,
                padding: "8px 10px",
              }}
            >
              <i className="fa fa-arrow-left-from-bracket" aria-hidden />
              <span className="label">Exit & Return to Admin</span>
            </button>
          </div>
        )}
        <div className="client-logo-wrap" title="GPS">
          <NavLink
            to="/"
            onClick={() => {
              window.scrollTo({ top: 0 });
              if (mobileOpen) setMobileOpen(false);
            }}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <img src="/images/gpslogo.png" alt="GPS logo" />
          </NavLink>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isMobile && (
              <button
                className="client-collapse-btn"
                onClick={toggleCollapse}
                title={collapsed ? "Expand menu" : "Collapse menu"}
                aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              >
                <i className={`fa ${collapsed ? "fa-chevron-right" : "fa-bars"}`} />
              </button>
            )}
            {isMobile && (
              <button
                className="client-collapse-btn"
                onClick={() => setMobileOpen(false)}
                title="Close menu"
                aria-label="Close menu"
                style={{ color: "#fff" }}
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>

        <nav className="client-sidebar-nav" role="navigation" aria-label="Main menu">
          <NavLink
            to="/overview"
            end
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Home"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-house" aria-hidden /></span>
            <span className="label">Home</span>
          </NavLink>

          <NavLink
            to="/portfolio-overview"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="My Portfolio"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-briefcase" aria-hidden /></span>
            <span className="label">My Portfolio</span>
          </NavLink>

          <NavLink
            to="/explore"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Explore Funds"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-magnifying-glass-chart" aria-hidden /></span>
            <span className="label">Explore Funds</span>
          </NavLink>

          <NavLink
            to="/set-goal"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Goals"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-bullseye" aria-hidden /></span>
            <span className="label">Goals</span>
          </NavLink>

          <NavLink
            to="/risk-profiling"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Risk Assessment"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-gauge-high" aria-hidden /></span>
            <span className="label">Risk Assessment</span>
          </NavLink>

          {/* Transact With Demat */}
          <div
            ref={transactRef}
            className={`client-menu-item ${inlineOpen === "transact" && !collapsed ? "active" : ""}`}
            data-label="Transact With Demat"
            role="button"
            onClick={() => toggleDropdown("transact")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "transact" : inlineOpen === "transact"}
          >
            <span className="icon"><i className="fa-solid fa-arrow-right-arrow-left" aria-hidden /></span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 8,
              }}
            >
              <span className="label">Transact With Demat</span>
              <i className={`fa fa-chevron-${inlineOpen === "transact" ? "up" : "down"}`} style={{ fontSize: 11 }} />
            </div>
          </div>
          {inlineOpen === "transact" && (!collapsed || isMobile) && (
            <div className="client-submenu" role="menu" aria-label="Transact submenu">
              <NavLink to="/invest-online-bse/new-investment" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-plus" /></span>
                <span className="label">New Investment</span>
              </NavLink>
              <NavLink to="/invest-online-bse/my-orders" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-list-ol" /></span>
                <span className="label">My Orders</span>
              </NavLink>
              <NavLink to="/sipinvest" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-calendar-plus" /></span>
                <span className="label">Start SIP</span>
              </NavLink>
              <NavLink to="/redeemMf" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
                <span className="label">Redeem</span>
              </NavLink>
              <NavLink to="/switchmf" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-right-left" /></span>
                <span className="label">Switch</span>
              </NavLink>
              <NavLink to="/mandatesmf" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-file-signature" /></span>
                <span className="label">Mandates / OTBM</span>
              </NavLink>
            </div>
          )}

          {/* My Account */}
          <div
            ref={accountRef}
            className={`client-menu-item ${inlineOpen === "account" && !collapsed ? "active" : ""}`}
            data-label="My Account"
            role="button"
            onClick={() => toggleDropdown("account")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "account" : inlineOpen === "account"}
          >
            <span className="icon"><i className="fa-regular fa-user" aria-hidden /></span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 8,
              }}
            >
              <span className="label">My Account</span>
              <i className={`fa fa-chevron-${inlineOpen === "account" ? "up" : "down"}`} style={{ fontSize: 11 }} />
            </div>
          </div>
          {inlineOpen === "account" && (!collapsed || isMobile) && (
            <div className="client-submenu" role="menu" aria-label="Account submenu">
              <NavLink to="/account/profile-kyc" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-id-card" /></span>
                <span className="label">Profile & KYC</span>
              </NavLink>
              <NavLink to="/account/banks-nominees" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-university" /></span>
                <span className="label">Banks & Nominees</span>
              </NavLink>
              <NavLink to="/my-folios" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-folder-open" /></span>
                <span className="label">My Folios</span>
              </NavLink>
              <NavLink to="/manageSIPs" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-calendar-check" /></span>
                <span className="label">My SIPs</span>
              </NavLink>
              <NavLink to="/documents" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-folder" /></span>
                <span className="label">Documents</span>
              </NavLink>
              <NavLink to="/reports/account-statement" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-file-invoice" /></span>
                <span className="label">Statements</span>
              </NavLink>
              <NavLink to="/account/security" className="client-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-shield-halved" /></span>
                <span className="label">Security</span>
              </NavLink>
            </div>
          )}

          <NavLink
            to="/transactions"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Transactions"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-receipt" aria-hidden /></span>
            <span className="label">Transactions</span>
          </NavLink>

          <NavLink
            to="/support/tickets"
            className={({ isActive }) => `client-menu-item ${isActive ? "active" : ""}`}
            data-label="Helpdesk"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-headset" aria-hidden /></span>
            <span className="label">Helpdesk</span>
          </NavLink>

          <button
            onClick={handleLogout}
            className="client-menu-item sidebar-logout"
            data-label="Logout"
            title="Logout"
            type="button"
          >
            <span className="icon"><i className="fa-solid fa-arrow-right-from-bracket" /></span>
            <span className="label">Logout</span>
          </button>
        </nav>
      </aside>

      {/* Backdrop for mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="client-mobile-backdrop"
          role="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating submenu when sidebar collapsed (desktop) */}
      {floatingOpen && !isMobile && (
        <div
          ref={floatingRef}
          className="client-floating-submenu"
          role="menu"
          aria-label={`${floatingOpen} submenu`}
          style={{ top: floatingTop }}
        >
          {floatingOpen === "account" && (
            <>
              <NavLink to="/account/profile-kyc" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-id-card" /></span>
                <span className="label">Profile & KYC</span>
              </NavLink>
              <NavLink to="/account/banks-nominees" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-university" /></span>
                <span className="label">Banks & Nominees</span>
              </NavLink>
              <NavLink to="/my-folios" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-folder-open" /></span>
                <span className="label">My Folios</span>
              </NavLink>
              <NavLink to="/manageSIPs" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-calendar-check" /></span>
                <span className="label">My SIPs</span>
              </NavLink>
              <NavLink to="/documents" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-folder" /></span>
                <span className="label">Documents</span>
              </NavLink>
              <NavLink to="/reports/account-statement" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-file-invoice" /></span>
                <span className="label">Statements</span>
              </NavLink>
              <NavLink to="/account/security" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-shield-halved" /></span>
                <span className="label">Security</span>
              </NavLink>
            </>
          )}
          {floatingOpen === "transact" && (
            <>
              <NavLink to="/invest-online-bse/new-investment" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-plus" /></span>
                <span className="label">New Investment</span>
              </NavLink>
              <NavLink to="/invest-online-bse/my-orders" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-list-ol" /></span>
                <span className="label">My Orders</span>
              </NavLink>
              <NavLink to="/sipinvest" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-calendar-plus" /></span>
                <span className="label">Start SIP</span>
              </NavLink>
              <NavLink to="/redeemMf" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
                <span className="label">Redeem</span>
              </NavLink>
              <NavLink to="/switchmf" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-right-left" /></span>
                <span className="label">Switch</span>
              </NavLink>
              <NavLink to="/mandatesmf" className="client-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-file-signature" /></span>
                <span className="label">Mandates / OTBM</span>
              </NavLink>
            </>
          )}
        </div>
      )}
    </>
  );
}
